import { create } from '@bufbuild/protobuf';
import {
  Timestamp,
  timestampDate,
  timestampFromDate,
} from '@bufbuild/protobuf/wkt';
import { MetricType } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/metrics_pb';
import { ProductType } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/system_pb';
import type {
  IDF,
  ListSimulationMetricsRequest_Target,
} from '@wavingroup/aqora-v2-crate/wavin/simulations/v1/simulations_service_pb';
import {
  ListSimulationMetricsRequest_TargetSchema,
  SimulationRun,
  SimulationRun_State,
  SimulationRun_TopologyState,
  SimulationRun_Type,
} from '@wavingroup/aqora-v2-crate/wavin/simulations/v1/simulations_service_pb';
import { TFunction } from 'i18next';
import { ReservoirModel } from '~/shared/models/system/ReservoirModel';
import { SystemModel } from '~/shared/models/system/SystemModel';
import {
  assertIsDefined,
  assertUnreachable,
  notEmpty,
} from '~/types/assert-type';

export class SimulationModel {
  private static readonly IDF_ACTUATOR_METRIC_TYPES = [
    MetricType.CALCULATED_VALVE_HEIGHT_PERCENTAGE,
    MetricType.VALVE_STATE_ON,
    MetricType.PUMP_STATE_ON,
  ] as const;

  readonly id: string;

  readonly title: string;

  readonly timeFrom: Timestamp;

  readonly timeFromDate: Date;

  readonly weekBeforeEnd: Timestamp;

  readonly twelveHoursBeforeEnd: Timestamp;

  readonly timeTo: Timestamp;

  readonly timeToDate: Date;

  readonly createdAt?: Timestamp;

  readonly state: SimulationRun_State;

  readonly type: SimulationRun_Type;

  readonly system: SystemModel;

  readonly done: boolean = false;

  readonly topologyOutdated: boolean = false;

  readonly idf: IDF | undefined;

  constructor(simulation: SimulationRun) {
    this.id = simulation.runAttemptName;
    this.title = simulation.title;
    assertIsDefined(simulation.system);
    assertIsDefined(simulation.timeFrom);
    assertIsDefined(simulation.timeTo);
    this.timeTo = simulation.timeTo;
    this.timeFrom = simulation.timeFrom;
    this.timeFromDate = timestampDate(simulation.timeFrom);
    this.timeToDate = timestampDate(simulation.timeTo);
    this.weekBeforeEnd = this.getWeekBeforeEnd();
    this.twelveHoursBeforeEnd = this.getTwelveHoursBeforeEnd();
    this.createdAt = simulation.createdAt;
    this.state = simulation.state;
    this.type = simulation.type;
    this.system = new SystemModel({
      system: simulation.system,
      isSimulation: true,
    });
    this.done = simulation.state === SimulationRun_State.DONE;
    this.topologyOutdated =
      simulation.topologyState === SimulationRun_TopologyState.DIFFERENT;
    this.idf =
      simulation.details?.case === 'idf' ? simulation.details.value : undefined;
  }

  getIdfConfigKey(): string | undefined {
    if (!this.idf) return undefined;

    return [
      this.idf?.intensity,
      this.idf?.durationSeconds.toString(),
      this.idf?.fillPercentageStart,
      this.idf?.timeBeforeEventSeconds.toString(),
      this.idf?.timeAfterEventSeconds.toString(),
      this.idf?.blueMode,
    ].join('|');
  }

  getIdfReportParameters() {
    if (!this.idf) {
      return null;
    }

    return {
      intensity: this.idf.intensity,
      durationMinutes: Number(this.idf.durationSeconds) / 60,
      fillPercentageStart: this.idf.fillPercentageStart,
      timeBeforeEventHours: Number(this.idf.timeBeforeEventSeconds) / 3600,
      timeAfterEventHours: Number(this.idf.timeAfterEventSeconds) / 3600,
      systemMode: this.idf.blueMode ? ('blue' as const) : ('green' as const),
    };
  }

  getStateLabel(t: TFunction): string {
    switch (this.state) {
      case SimulationRun_State.WORKING:
        return t('simulations.state.working');
      case SimulationRun_State.CREATED:
        return t('simulations.state.created');
      case SimulationRun_State.DONE:
        return t('simulations.state.done');
      case SimulationRun_State.FAILED:
        return t('simulations.state.failed');
      case SimulationRun_State.UNSPECIFIED:
        throw new Error('Unspecified simulation state');
      default:
        return assertUnreachable(this.state);
    }
  }

  private getWeekBeforeEnd(): Timestamp {
    const weekBeforeEnd = timestampDate(this.timeTo);
    weekBeforeEnd.setDate(weekBeforeEnd.getDate() - 7);
    return timestampFromDate(weekBeforeEnd);
  }

  private getTwelveHoursBeforeEnd(): Timestamp {
    const twelveHoursBeforeEnd = timestampDate(this.timeTo);
    twelveHoursBeforeEnd.setHours(twelveHoursBeforeEnd.getHours() - 12);
    return timestampFromDate(twelveHoursBeforeEnd);
  }

  getTopReservoirsByCapacity() {
    return [...this.system.reservoirs]
      .sort((a, b) => b.totalCapacity - a.totalCapacity)
      .slice(0, 3);
  }

  getIdfChartMetricsTargets(): ListSimulationMetricsRequest_Target[] {
    const topReservoirs = this.getTopReservoirsByCapacity();

    const reservoirTargets = topReservoirs.map((reservoir) =>
      create(ListSimulationMetricsRequest_TargetSchema, {
        resourceName: reservoir.name,
        types: [MetricType.CALCULATED_WATER_PERCENTAGE],
      }),
    );

    const precipitationTarget = this.getPrecipitationTarget();

    const actuatorTargets = this.buildActuatorTargets(topReservoirs);

    return [
      ...reservoirTargets,
      precipitationTarget,
      ...actuatorTargets,
    ].filter(notEmpty);
  }

  private getPrecipitationTarget():
    | ListSimulationMetricsRequest_Target
    | undefined {
    const rainMeter = this.system.products.find(
      (p) => p.type === ProductType.RAIN_METER,
    );

    const precipitationTarget = rainMeter?.availableMetricTargets.find((t) =>
      t.types.includes(MetricType.PRECIPITATION_MM),
    ) as unknown as ListSimulationMetricsRequest_Target | undefined;

    return precipitationTarget;
  }

  private buildActuatorTargets(
    topReservoirs: readonly ReservoirModel[],
  ): ListSimulationMetricsRequest_Target[] {
    return topReservoirs.flatMap((reservoir) => {
      const availableTargets = reservoir.products.flatMap(
        (product) => product.availableMetricTargets,
      );

      for (const metricType of SimulationModel.IDF_ACTUATOR_METRIC_TYPES) {
        const target = availableTargets.find((availableTarget) =>
          availableTarget.types.includes(metricType),
        );

        if (target) {
          return [
            create(ListSimulationMetricsRequest_TargetSchema, {
              resourceName: target.resourceName,
              types: [metricType],
            }),
          ];
        }
      }

      return [];
    });
  }
}
